import { log } from './log';
import { injectHorizontalPlayerAssets } from '../utils/injectHorizontalPlayer';
import { extractContextualVideoFromPlaylist, hasQueryStringVar } from './inject-common';

let pixelsAdTag: string;
let publisherId: string;
let playlistId = 'sport';
let bidConfig: Record<string, any>[];
let contextualEnabled = true;
let playlistEnabled = true;
const originalOverrideKey = 'has_horizontal_match';
let overrideKey = originalOverrideKey;
let pixelsPageId: string;
let pixelsPlaylist: PlaylistItem[];
const playerDivId = 'pixels_inject_video_player_inner';
let isProd = true;
const ENABLED_ON_PROD = true;
const isProdCheck = (domain: string) => /pub.pixels.ai/.test(domain);
const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

const pixelsMatchBaseUrl = __MATCH_VIDEO_ENDPOINT__;

const getPlaylist = async (pixelsPageId: string): Promise<PlaylistItem[]> => {
  if (hasQueryStringVar(overrideKey)) {
    log('pixels', 'injecting test match...');
    // sample clock countdown
    return [
      {
        media_id: 'az2htL700BK7eyBnf00Ojc56G7nOQyEvgUgrXtnsDDZ02w',
        headline: '',
        animated_preview: '',
        thumbnail: '',
        duration: 0,
        rightsholder: '',
        contextual: true,
      },
    ];
  }

  const endpoint = new URL(pixelsMatchBaseUrl);
  endpoint.pathname = endpoint.pathname.replace(/\/$/, '') + `/horizontal/${publisherId}/${playlistId}/${pixelsPageId}`;
  log('pixels', 'endpoint', endpoint.toString());
  try {
    const playlist = await (await fetch(endpoint.toString())).json();
    log('pixels', 'outstream playlist', playlist);
    return playlist;
  } catch (_) {
    log('pixels', 'outstream match NOT found');
    return [];
  }
};

const renderOutstream = (elementId: string) => {
  const pixelsInjectAfterPoint = document.querySelector(elementId) as HTMLElement;
  const pixelsInjectVideoPlayer = document.createElement<'div'>('div');
  pixelsInjectVideoPlayer.style.backgroundColor = 'black';
  pixelsInjectVideoPlayer.style.width = '100%';
  pixelsInjectVideoPlayer.style.height = '0';
  pixelsInjectVideoPlayer.style.paddingBottom = '56.25%';
  pixelsInjectAfterPoint.appendChild(pixelsInjectVideoPlayer);

  // add div for player inner
  const pixelsInjectVideoPlayerInner = document.createElement('div');
  pixelsInjectVideoPlayerInner.id = playerDivId;
  pixelsInjectVideoPlayer.appendChild(pixelsInjectVideoPlayerInner);
};

const injectOutstream = (elementId: string) => {
  renderOutstream(elementId);
};

export const pixelsInject: InjectPixelsOutstream = (elementId: string) => {
  return {
    setup: async (options: PixelsOutstreamSetupParam, deliveryCap: number = null) => {
      return pixelsOutstream(elementId).setup(options, deliveryCap);
    },
    setAdTag: (adTag: string) => {
      pixelsOutstream(elementId).setAdTag(adTag);
    },
    setPubId: (pubId: string) => {
      pixelsOutstream(elementId).setPubId(pubId);
    },
    setPlaylist: (playlist: string) => {
      pixelsOutstream(elementId).setPlaylist(playlist);
    },
    disableContextual: () => {
      pixelsOutstream(elementId).disableContextual();
    },
    disablePlaylist: () => {
      pixelsOutstream(elementId).disablePlaylist();
    },
  };
};

const removeContextualVideoFromPlaylist = (playlist: any[]): any[] => {
  if (playlist[0]?.contextual) {
    playlist.shift();
  }
  return playlist;
};

const withinDeliveryCap = (deliveryCap: number) => {
  if (!deliveryCap) {
    return true;
  }
  const randomNumber = Math.random();
  return randomNumber <= deliveryCap;
};

const hasContextualVideo = (playlist: any[]): boolean => {
  return playlist[0]?.contextual;
};

const initializeHorizontalPlayer = async () => {
  if (pixelsPlaylist) {
    const { _horizontalPixels } = window;

    _horizontalPixels(playerDivId, {
      pageId: pixelsPageId,
      playlist: pixelsPlaylist,
      playlistId: playlistId,
      adTagUrl: pixelsAdTag,
      bidConfig: bidConfig,
    });
  }
};

export const pixelsOutstream = (elementId: string) => {
  return {
    setup: async (options: PixelsOutstreamSetupParam, deliveryCap: number = null): Promise<boolean> => {
      pixelsPageId = options?.pageId;
      // only inject video in test envs for now...
      if (ENABLED_ON_PROD || !isProd) {
        if (pixelsPageId) {
          pixelsPlaylist = await getPlaylist(pixelsPageId);
          if (!contextualEnabled) {
            pixelsPlaylist = removeContextualVideoFromPlaylist(pixelsPlaylist);
          } else if (!playlistEnabled) {
            pixelsPlaylist = extractContextualVideoFromPlaylist(pixelsPlaylist);
          }
          // only inject if we have a video...
          let hasContextualVideoIfRequired = true;
          if (options.requireContextualForOutstream) {
            hasContextualVideoIfRequired = hasContextualVideo(pixelsPlaylist);
          }
          if (pixelsPlaylist.length > 0 && hasContextualVideoIfRequired) {
            if (!withinDeliveryCap(deliveryCap)) {
              // circuit breaker...
              return false;
            }
            // allow some time for host page to settle before querying hero image rect...
            log('pixels', 'outstream called with pixelsPageId and pixelsVideoId', pixelsPageId, pixelsPlaylist);
            await sleep(250);
            injectOutstream(elementId);
            await injectHorizontalPlayerAssets(options.organisation || publisherId);
            initializeHorizontalPlayer();
          } else if (pixelsPlaylist.length > 0 && options.requireContextualForOutstream) {
            // atrtennis mid article
            return false;
          }
        }
      } else {
        log('pixels', 'not running on prod...');
      }

      return Array.isArray(pixelsPlaylist) && pixelsPlaylist.length > 0;
    },
    setAdTag: (adTag: string) => {
      pixelsAdTag = adTag;
    },
    setBidConfig: (config: Record<string, any>[]) => {
      bidConfig = config;
    },
    setPubId: (pubId: string) => {
      if (pubId) {
        publisherId = pubId;
        log('pixels', 'outstream', 'publisherId', publisherId);
      }
    },
    setPlaylist: (playlist: string) => {
      if (playlist) {
        playlistId = playlist;
        log('pixels', 'outstream', 'playlistSlug', playlistId);
      }
    },
    disableContextual: () => {
      contextualEnabled = false;
      log('pixels', 'outstream', 'contextual disabled');
    },
    disablePlaylist: () => {
      playlistEnabled = false;
      log('pixels', 'outstream', 'playlist disabled');
    },
    setOverrideKey: (key: string) => {
      overrideKey = key;
      log('pixels', 'outstream', 'overrideKey', key);
    },
    resetOverrideKey: () => {
      overrideKey = originalOverrideKey;
      log('pixels', 'outstream', 'overrideKey reset');
    },
  };
};

try {
  // this won't work on IE 11 and below but I don't think it matters
  // because we don't support them?
  let currentSrc = document.currentScript?.getAttribute('src');
  if (!currentSrc.startsWith('http')) {
    currentSrc = window.location.origin + currentSrc;
  }
  isProd = isProdCheck(currentSrc);
  const assetUrl = new URL(currentSrc);
  const pubId = assetUrl.searchParams.get('id');
  if (pubId) {
    pixelsOutstream('').setPubId(pubId);
  }
} catch (exception) {
  console.error(exception);
}
